import * as React from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/styles/variants/buttons';

export interface TabButtonProps {
  variant?: any;
}
interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  isScrollable?: boolean;
}
const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, isScrollable, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        isScrollable
          ? 'inline-flex w-fit max-w-[100vw] overflow-x-auto scrollbar-hide'
          : 'inline-flex items-center justify-center rounded-full',
        className
      )}
      {...props}
    />
  )
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & TabButtonProps
>(({ className, variant, ...props }, ref) => {
  const [colorVariant, setColorVariant] = React.useState<any>('');
  //to set the sctive tab colors as per contentful value
  React.useEffect(() => {
    if (variant) {
      switch (variant) {
        case 'primary':
          setColorVariant(
            'data-[state=active]:bg-color-blue-brightBlue100 data-[state=active]:text-color-white-almostWhite10  data-[state=active]:hover:bg-color-blue-darkBlue100 data-[state=active]:border-none'
          );
          break;
        case 'default':
          setColorVariant(
            'data-[state=active]:bg-color-white-almostWhite10 data-[state=active]:text-color-blue-brightBlue100 data-[state=active]:hover:bg-color-white-almostWhite10 data-[state=active]:hover:text-color-blue-brightBlue100 data-[state=active]:hover:border-color-white-almostWhite10'
            // data-[state=active]:hover:bg-color-blue-darkBlue100 data-[state=active]:hover:text-color-white-almostWhite10
          );
          break;
        case 'tertiary':
          setColorVariant(
            'data-[state=active]:border-0 data-[state=active]:text-color-white-almostWhite10 data-[state=active]:underline data-[state=active]:hover:text-color-blue-brightBlue100 data-[state=active]:hover:bg-transparent'
          );
          break;
        case 'secondary':
          setColorVariant(
            'data-[state=active]:bg-transparent data-[state=active]:text-color-white-almostWhite10 data-[state=active]:border-2 data-[state=active]:border-color-white-almostWhite10 data-[state=active]:hover:bg-color-blue-darkBlue100 data-[state=active]:hover:border-color-blue-darkBlue100'
          );
          break;
        default:
          break;
      }
    }
  }, [variant]);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        colorVariant,
        buttonVariants({
          variant: 'secondary',
          className:
            'hover:border-color-white-almostWhite10 hover:bg-color-white-almostWhite10 hover:text-color-blue-brightBlue100',
        }),
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-2 focus-visible:outline-none', className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

const CustomTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'data-[state=active]:bg-color-blue-brightBlue100 data-[state=active]:font-bold data-[state=active]:text-color-white-white100',
      buttonVariants({
        variant: 'secondary',
        className:
          'hover:border-color-blue-brightBlue100 hover:bg-color-blue-brightBlue100 hover:text-color-white-white100',
      }),
      className
    )}
    {...props}
  />
));
CustomTabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, CustomTabsTrigger };
